// App Radar — entry point.
// Search -> resolve app -> multi-region lookup -> render comparison table ->
// (M3) enrich each region with its Apple top-chart rank when enabled.
//
// Shared links: the resolved app's id is written to the URL as `?appId=...`, and
// on load that param (if present) opens the comparison directly without a search.
import { searchApps, lookupAppByCountry, lookupAppById, lookupDeveloperApps } from './api/itunes.js';
import { parseAppStoreUrl } from './domain/parseAppStoreUrl.js';
import { STOREFRONTS, searchCountryForLocale, searchLangForCountry, searchLangForLocale } from './data/storefronts.js';
import { renderAppSummary, renderResultsTable, clearResults, rerenderResults, renderDeveloperApps, renderDualAppTable } from './ui/renderResults.js';
import { streamLookups } from './domain/streamLookups.js';
import { setLoading, setError, setIdle, clearStatus } from './ui/renderStatus.js';
import { CHART_CONFIG, isChartEnrichmentEnabled } from './config.js';
import { chartStandingFor, mapWithConcurrency, clearChartCache } from './api/rssCharts.js';
import { t, getLocale, setLocale, onLocaleChange, applyStaticTranslations, countryName } from './i18n/index.js';
import { recordSearch, getTopSearches } from './domain/searchHistory.js';
import { updateMetaTags, resetMetaTags } from './ui/metaTags.js';
import { renderWatchlist, renderPinButton } from './ui/renderWatchlist.js';
import { getWatchlist } from './domain/watchlist.js';
import { getStoredSearchRegion, setSearchRegion, clearSearchRegion } from './domain/searchRegion.js';
import { APP_COUNTRY_QUERY_KEY, appCountryFromParams, buildAppComparisonHref } from './domain/appComparisonLink.js';
import { GENRES, genreName } from './data/genres.js';
import { renderVersionNotes } from './ui/renderVersionNotes.js';
import { renderCategoryLeaders } from './ui/renderCategoryLeaders.js';
import { rankByPopularity } from './domain/popularityScore.js';
import { initTheme } from './ui/theme.js';
import { renderExploreGrid, hideExploreGrid } from './ui/renderExploreGrid.js';
import { saveSnapshot, getDeltas, isoDateLabel } from './domain/rankSnapshots.js';
import { saveAppSnapshot, loadAppSnapshot, detectChanges } from './domain/changeMonitor.js';
import { renderChangeNotice } from './ui/renderChangeNotice.js';
import { renderMarketGap } from './ui/renderMarketGap.js';
import { renderMarketOpportunity } from './ui/renderMarketOpportunity.js';
import { computeMovers } from './domain/trendingDigest.js';
import { renderTrendingDigest } from './ui/renderTrendingDigest.js';
import { renderChartBrowser, hideChartBrowser } from './ui/renderChartBrowser.js';
import { loadOfflineComparison, saveLastComparison } from './domain/cachedComparison.js';
import { bindOpportunityRadarLocaleRefresh, loadOpportunityRadar } from './ui/renderOpportunityRadar.js';
import { loadDailyInsightReport } from './ui/renderDailyInsightReport.js';

// Cross-app comparison state
let compareApp = null;
let compareRows = [];
let currentPrimaryApp = null;

/**
 * Reflect the resolved app id into the URL so the view is shareable.
 * Uses replaceState so it does not spam browser history on every search.
 * @param {number|string|null} appId
 * @param {number|string|null} [compareId]
 */
function syncUrl(appId, compareId) {
  const url = new URL(window.location.href);
  if (appId) {
    url.searchParams.set('appId', String(appId));
  } else {
    url.searchParams.delete('appId');
  }
  url.searchParams.delete(APP_COUNTRY_QUERY_KEY);
  if (compareId) {
    url.searchParams.set('compareId', String(compareId));
  } else {
    url.searchParams.delete('compareId');
  }
  window.history.replaceState({}, '', url);
}

/**
 * Run the multi-region lookup for an already-resolved app and render the table.
 * @param {object} app - Raw iTunes result (must have trackId / trackName).
 */
/** @type {import('./domain/normalizeApp.js').RegionRow[]} */
let primaryRows = [];

async function loadComparison(app) {
  currentPrimaryApp = app;
  renderAppSummary(app);
  syncUrl(app.trackId, compareApp?.trackId ?? null);
  updateMetaTags(app);

  // Stream results: show each region row as its lookup resolves rather than
  // waiting for all 28 storefronts before rendering anything.
  const streamedRows = [];
  setLoading(t('status.loadingProgress', { current: 0, total: STOREFRONTS.length }));

  const rows = await streamLookups(STOREFRONTS, lookupAppByCountry, app.trackId, (_row, completed, total) => {
    streamedRows.push(_row);
    setLoading(t('status.loadingProgress', { current: completed, total }));
    if (compareApp && compareRows.length > 0) {
      renderDualAppTable([...streamedRows], compareRows, app, compareApp);
    } else {
      renderResultsTable([...streamedRows]);
    }
  });

  // Compute popularity scores once so we can diff against the stored snapshot.
  const scoredRows = rankByPopularity(rows);

  // Version/price change detection: load prior snapshot before saving the new one.
  const priorChangeSnap = loadAppSnapshot(app.trackId);
  const appChanges = detectChanges(priorChangeSnap, scoredRows);
  renderChangeNotice(
    appChanges,
    (cc) => countryName(cc, cc.toUpperCase()),
    document.getElementById('change-notice-container'),
  );

  // Snapshot delta: compare scored rows to the most recent prior snapshot.
  const { deltas, snappedAt } = getDeltas(app.trackId, scoredRows);
  const rowsWithDelta = scoredRows.map((row) => ({
    ...row,
    popularityDelta: deltas.get(row.countryCode) ?? null,
  }));

  primaryRows = rowsWithDelta;

  // Final render with all rows so sorting and contrast are computed over the
  // complete dataset rather than the arrival-ordered partial set.
  if (compareApp && compareRows.length > 0) {
    renderDualAppTable(rowsWithDelta, compareRows, app, compareApp);
  } else {
    renderResultsTable(rowsWithDelta);
  }
  saveLastComparison(app, rowsWithDelta);
  // Show "first look" note when there's no prior snapshot to diff against.
  if (!snappedAt) {
    setIdle(t('delta.firstLook'));
  } else {
    clearStatus();
  }

  // Save this view as a snapshot for future delta computation.
  // Saved after rendering so the current session always sees the prior-visit delta.
  const nowIso = new Date().toISOString();
  saveSnapshot(app.trackId, scoredRows, nowIso);
  saveAppSnapshot(app.trackId, scoredRows);

  // Market Opportunity panel (issue #118): a purely client-side, derived index
  // ranking storefronts by where to launch / localize next. Built from the rows
  // already loaded (rating volume, language count, release recency, and the
  // prior-visit popularity delta) — no extra network calls.
  renderMarketOpportunity(
    primaryRows,
    document.getElementById('market-opportunity-container'),
    { nowMs: Date.now() },
  );

  // Version notes section: surfaces per-country version and release note diffs.
  renderVersionNotes(rows, document.getElementById('version-notes-container'));

  // Category leaders panel: show top-5 free/paid in the same genre for a country.
  // primaryGenreId comes from the first available row; falls back to null (panel hidden).
  const primaryGenreId = app.primaryGenreId ? String(app.primaryGenreId) : null;
  const defaultCountry = getEffectiveSearchRegion();
  renderCategoryLeaders({
    genreId: primaryGenreId,
    defaultCountry,
    container: document.getElementById('category-leaders-container'),
  });

  // Pin button in app-summary header — re-renders watchlist on toggle.
  renderPinButton(app, () => renderWatchlist((id) => runById(id)));

  // Cross-app comparison bar (appears after primary comparison loads).
  initCompareBar(app);

  // M3: enrich with Apple top-chart rank. Disabled by default because the RSS
  // feeds are not CORS-accessible from a static browser origin (see config.js).
  // Chart failures stay isolated to the chart fields and never erase rows.
  if (isChartEnrichmentEnabled()) {
    await enrichWithChartRanks(rows, app.trackId, primaryRows);
  }

  // Developer profile: load other apps by the same developer asynchronously so
  // it never delays the main comparison. Silently skip if artistId is absent.
  const artistId = app.artistId;
  if (artistId) {
    lookupDeveloperApps(artistId, getEffectiveSearchRegion(), 10, app.trackId)
      .then((devApps) => {
        renderDeveloperApps(devApps, app.sellerName || app.artistName || '', (trackId) => runById(trackId));
      })
      .catch(() => {
        renderDeveloperApps(null, '', null);
      });
  } else {
    renderDeveloperApps(null, '', null);
  }
}

/**
 * Show the cold-start Explore grid if no comparison is active.
 * Clicking a card triggers runById and hides the grid.
 */
function showExploreGrid() {
  const container = document.getElementById('explore-grid-container');
  renderExploreGrid(container, (trackId) => {
    hideExploreGrid(document.getElementById('explore-grid-container'));
    runById(trackId);
  });
}

function renderOfflineCacheBanner(show) {
  const node = document.getElementById('offline-cache-banner');
  if (!node) return;
  node.hidden = !show;
  node.textContent = show ? t('offline.cachedBanner') : '';
}

function hydrateOfflineComparison() {
  const cached = loadOfflineComparison();
  if (!cached) {
    renderOfflineCacheBanner(false);
    return false;
  }

  hideExploreGrid(document.getElementById('explore-grid-container'));
  hideChartBrowser(document.getElementById('chart-browser-container'));
  clearStatus();

  currentPrimaryApp = cached.app;
  primaryRows = cached.rows;
  compareApp = null;
  compareRows = [];

  renderAppSummary(cached.app);
  renderResultsTable(cached.rows);
  renderMarketOpportunity(
    cached.rows,
    document.getElementById('market-opportunity-container'),
    { nowMs: Date.now() },
  );
  renderVersionNotes(cached.rows, document.getElementById('version-notes-container'));
  renderPinButton(cached.app, () => renderWatchlist((id) => runById(id)));
  initCompareBar(cached.app);
  updateMetaTags(cached.app);
  renderOfflineCacheBanner(true);
  return true;
}

/**
 * Render the popular-searches leaderboard from localStorage history.
 * Hidden when the history is empty.
 */
function renderLeaderboard() {
  const section = document.getElementById('leaderboard');
  if (!section) return;
  const top = getTopSearches(8);
  if (top.length === 0) {
    section.hidden = true;
    section.innerHTML = '';
    return;
  }
  const chips = top
    .map(
      ({ query, count }) =>
        `<li><button class="leaderboard__chip" type="button" data-query="${query.replace(/"/g, '&quot;')}">${query}<span class="leaderboard__chip-count">${count}</span></button></li>`,
    )
    .join('');
  section.hidden = false;
  section.innerHTML = `<p class="leaderboard__title">${t('leaderboard.title')}</p><ul class="leaderboard__chips">${chips}</ul>`;
  section.querySelectorAll('.leaderboard__chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      const query = btn.dataset.query;
      const input = document.getElementById('search-input');
      if (input) input.value = query;
      runSearch(query);
    });
  });
}

/**
 * Resolve an app from a free-text query and load its comparison.
 * @param {string} query
 */
async function runSearch(query) {
  renderOfflineCacheBanner(false);
  hideExploreGrid(document.getElementById('explore-grid-container'));
  hideChartBrowser(document.getElementById('chart-browser-container'));
  clearStatus();
  clearResults();
  renderAppSummary(null);
  resetMetaTags();

  setLoading(t('status.searching', { query }));

  let app;
  try {
    app = await searchApps(query, getEffectiveSearchRegion(), 1, getSelectedGenre(), getSelectedPlatform(), getEffectiveSearchLang());
  } catch (err) {
    setError(t('error.searchFailed', { message: err.message }));
    return;
  }

  if (!app) {
    setError(t('error.noResult', { query }));
    return;
  }

  recordSearch(query);
  renderLeaderboard();
  await loadComparison(app);
}

/**
 * Resolve an app by id (shared `?appId=` link) and load its comparison.
 * @param {string} appId
 * @param {string|null} [sourceCountry]
 */
async function runById(appId, sourceCountry = null) {
  renderOfflineCacheBanner(false);
  hideExploreGrid(document.getElementById('explore-grid-container'));
  hideChartBrowser(document.getElementById('chart-browser-container'));
  clearStatus();
  clearResults();
  renderAppSummary(null);

  setLoading(t('status.openingShared'));

  let app;
  try {
    app = await lookupAppById(appId, sourceCountry ?? getEffectiveSearchRegion());
  } catch (err) {
    setError(t('error.loadById', { appId, message: err.message }));
    return;
  }

  if (!app) {
    setError(t('error.noAppId', { appId }));
    syncUrl(null);
    return;
  }

  await loadComparison(app);
}

/**
 * Look up each available row's chart standing and re-render with the results.
 * Mutates the chart-* fields on the rows in place, then re-renders the table.
 * @param {import('./domain/normalizeApp.js').RegionRow[]} rows
 * @param {number|string} trackId
 * @param {import('./domain/normalizeApp.js').RegionRow[]} primaryRows - the
 *   delta-annotated rows from the originating load, captured at call time so a
 *   concurrent load reassigning the module-level state can't merge into them.
 */
async function enrichWithChartRanks(rows, trackId, primaryRows) {
  clearChartCache();
  const targets = rows.filter((r) => r.available && !r.error);
  if (targets.length === 0) return;

  setLoading(t('status.checkingCharts'));
  await mapWithConcurrency(targets, CHART_CONFIG.concurrency, async (row) => {
    try {
      const chart = await chartStandingFor(trackId, row.countryCode, CHART_CONFIG.limit);
      Object.assign(row, chart);
    } catch {
      // Defensive: chartStandingFor already folds failures into a RowChart, so
      // this should not throw — but if it ever does, keep the row intact.
      row.chartUnavailable = true;
      row.chartLabel = 'Chart unavailable';
    }
  });

  renderResultsTable(rows);
  if (currentPrimaryApp) {
    saveLastComparison(currentPrimaryApp, rows);
  }
  clearStatus();

  // Render market gap panel after chart data is loaded.
  renderMarketGap(rows, document.getElementById('market-gap-container'));

  // Re-rank opportunity now that chart-visibility data exists. Merge the chart
  // fields into the delta-annotated primaryRows so both the momentum and the
  // chart-visibility factors participate in the score.
  const chartByCc = new Map(rows.map((r) => [r.countryCode, r]));
  const mergedForOpportunity = primaryRows.map((r) => {
    const c = chartByCc.get(r.countryCode);
    return c
      ? { ...r, chartRankFree: c.chartRankFree, chartRankPaid: c.chartRankPaid, chartRankGrossing: c.chartRankGrossing }
      : r;
  });
  renderMarketOpportunity(
    mergedForOpportunity,
    document.getElementById('market-opportunity-container'),
    { nowMs: Date.now() },
  );
}

/**
 * Return the country code to use for the initial search.
 * Prefers the user's explicit selection; falls back to the locale-derived default.
 * @returns {string}
 */
function getEffectiveSearchRegion() {
  return getStoredSearchRegion() ?? searchCountryForLocale(getLocale());
}

/**
 * Return the language code to use for the initial search.
 * Explicit storefront selections use that storefront's primary language; Auto
 * follows the active UI locale's default storefront.
 * @returns {string|null}
 */
function getEffectiveSearchLang() {
  const stored = getStoredSearchRegion();
  return stored ? searchLangForCountry(stored) : searchLangForLocale(getLocale());
}

/**
 * Populate and wire the search-region selector dropdown.
 * Adds an "Auto" option first, then one option per known storefront.
 * Persists the selection to localStorage on change.
 */
function initRegionSelector() {
  const select = document.getElementById('search-region-select');
  if (!select) return;

  // Build option list: auto first, then all storefronts
  const autoOpt = document.createElement('option');
  autoOpt.value = '';
  autoOpt.textContent = t('search.region.auto');
  select.appendChild(autoOpt);

  for (const sf of STOREFRONTS) {
    const opt = document.createElement('option');
    opt.value = sf.code;
    opt.textContent = `${countryName(sf.code, getLocale(), sf.name)} (${sf.code.toUpperCase()})`;
    select.appendChild(opt);
  }

  // Restore persisted preference
  const stored = getStoredSearchRegion();
  select.value = stored ?? '';

  select.addEventListener('change', () => {
    const val = select.value;
    if (val) {
      setSearchRegion(val);
    } else {
      // "Auto" selected — clear stored preference so locale default kicks in
      clearSearchRegion();
    }
  });
}

/**
 * Return the selected genre ID, or null when "All categories" is chosen.
 * @returns {string|null}
 */
function getSelectedGenre() {
  const select = document.getElementById('search-genre-select');
  return (select && select.value) ? select.value : null;
}

/**
 * Return the selected platform: 'ios' (default) or 'mac'.
 * @returns {'ios'|'mac'}
 */
function getSelectedPlatform() {
  const select = document.getElementById('search-platform-select');
  return (select && select.value === 'mac') ? 'mac' : 'ios';
}

/**
 * Populate and wire the genre/category selector dropdown.
 */
function initGenreSelector() {
  const select = document.getElementById('search-genre-select');
  if (!select) return;

  // "All categories" first
  const allOpt = document.createElement('option');
  allOpt.value = '';
  allOpt.textContent = t('search.genre.all');
  select.appendChild(allOpt);

  const locale = getLocale();
  for (const genre of GENRES) {
    const opt = document.createElement('option');
    opt.value = genre.id;
    opt.textContent = genreName(genre.id, locale);
    select.appendChild(opt);
  }
}

/**
 * Wire the language switcher and apply the initial translations. On locale
 * change: re-translate the static chrome, re-render the results table (so
 * column headers, badges and the insight line switch language), and refresh
 * the idle hint if that is what is currently shown.
 */
function initI18n() {
  document.documentElement.setAttribute('lang', getLocale());
  applyStaticTranslations();
  updateTopNavHrefs();

  onLocaleChange(() => {
    applyStaticTranslations();
    updateTopNavHrefs();
    rerenderResults();
    renderLeaderboard();
    // Re-render explore grid with updated locale strings if it is currently visible.
    const exploreContainer = document.getElementById('explore-grid-container');
    if (exploreContainer && !exploreContainer.hidden) {
      showExploreGrid();
    }
    // Update translated option labels in the region and genre selectors.
    const regionSelect = document.getElementById('search-region-select');
    if (regionSelect && regionSelect.options.length > 0) {
      regionSelect.options[0].textContent = t('search.region.auto');
      for (const opt of regionSelect.options) {
        if (opt.value) opt.textContent = `${countryName(opt.value, getLocale(), opt.value.toUpperCase())} (${opt.value.toUpperCase()})`;
      }
    }
    const genreSelect = document.getElementById('search-genre-select');
    if (genreSelect && genreSelect.options.length > 0) {
      genreSelect.options[0].textContent = t('search.genre.all');
      // Re-translate the genre option labels in place (selection is preserved).
      const genreLocale = getLocale();
      for (const opt of genreSelect.options) {
        if (opt.value) opt.textContent = genreName(opt.value, genreLocale);
      }
    }
    const platformSelect = document.getElementById('search-platform-select');
    if (platformSelect && platformSelect.options.length >= 2) {
      platformSelect.options[0].textContent = t('search.platform.ios');
      platformSelect.options[1].textContent = t('search.platform.mac');
    }
    // Keep the idle hint in sync; loading/error lines are transient.
    const status = document.getElementById('status');
    if (status && status.classList.contains('status--idle')) {
      setIdle(t('status.idle'));
    }
  });
}

function localizedRoute(path) {
  return localizedPathForLocale(path, getLocale());
}

function localizedPathForLocale(path, locale) {
  const normalized = stripLocalePrefix(path || '/');
  if (locale === 'zh-CN') return `/zh${normalized === '/' ? '/' : normalized}`;
  if (locale === 'zh-Hant') return `/zh-Hant${normalized === '/' ? '/' : normalized}`;
  if (locale === 'ja') return `/ja${normalized === '/' ? '/' : normalized}`;
  return normalized;
}

function stripLocalePrefix(path) {
  const normalized = String(path || '/');
  const match = normalized.match(/^\/(zh|zh-CN|zh-Hans|zh-Hant|zh-TW|zh-HK|ja|jp|en)(\/.*)?$/i);
  if (!match) return normalized;
  return match[2] || '/';
}

function routePathForNav(path) {
  if (path === '/') return '/';
  const withoutLocale = stripLocalePrefix(path);
  return withoutLocale.endsWith('/') ? withoutLocale : `${withoutLocale}/`;
}

function updateActiveTopNav() {
  const currentPath = routePathForNav(window.location.pathname);
  const links = document.querySelectorAll('.topnav__link');
  for (const link of links) {
    const href = routePathForNav(link.getAttribute('href') || '/');
    const active = href === currentPath || (href === '/daily-insight/' && currentPath.startsWith('/daily-insight/'));
    link.classList.toggle('topnav__link--active', active);
  }
}

function syncLocaleToPath() {
  const pathLocale = window.location.pathname.startsWith('/zh/') || window.location.pathname === '/zh'
    ? 'zh-CN'
    : window.location.pathname.startsWith('/zh-Hant/') || window.location.pathname === '/zh-Hant'
      ? 'zh-Hant'
      : window.location.pathname.startsWith('/ja/') || window.location.pathname === '/ja'
        ? 'ja'
        : 'en';
  setLocale(pathLocale);
}

function updateTopNavHrefs() {
  syncLocaleToPath();
  const brand = document.querySelector('.brand');
  if (brand) brand.setAttribute('href', localizedRoute('/'));

  const routes = [
    ['nav.compare', '/'],
    ['nav.radar', '/radar/'],
    ['nav.opportunities', '/opportunities/'],
    ['nav.dailyInsight', '/daily-insight/'],
  ];
  for (const [key, path] of routes) {
    const link = document.querySelector(`[data-i18n="${key}"]`);
    if (link) link.setAttribute('href', localizedRoute(path));
  }
  updateActiveTopNav();
}

/**
 * Old HTML may be served from an English route with a stale Chinese storage
 * value. Route prefixes are the source of truth for navigation chrome.
 */
function enforceRouteLocaleBeforeRender() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '/';
  if (path === '/' || path.startsWith('/radar/') || path.startsWith('/opportunities/') || path.startsWith('/daily-insight/')) {
    setLocale('en');
  }
}

enforceRouteLocaleBeforeRender();

/**
 * Wire the "Compare another app" bar: toggle visibility, search, and clear.
 * @param {object} primaryApp - The primary app currently being compared.
 */
function initCompareBar(primaryApp) {
  const toggle = document.getElementById('compare-toggle');
  const form = document.getElementById('compare-form');
  const input = document.getElementById('compare-input');
  const clearBtn = document.getElementById('compare-clear');
  if (!toggle || !form || !input) return;

  toggle.addEventListener('click', () => {
    const willOpen = form.hidden;
    form.hidden = !willOpen;
    toggle.textContent = willOpen ? t('compare.toggleClose') : t('compare.toggle');
    if (willOpen) input.focus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const query = input.value.trim();
    if (!query) return;

    clearBtn.hidden = false;
    setLoading(t('compare.loading'));
    try {
      const extracted = parseAppStoreUrl(query);
      let app;
      if (extracted || /^\d+$/.test(query)) {
        app = await lookupAppById(extracted ?? query, getEffectiveSearchRegion());
      } else {
        app = await searchApps(query, getEffectiveSearchRegion(), 1, null, 'ios', getEffectiveSearchLang());
      }
      if (!app) {
        setError(t('error.noResult', { query }));
        return;
      }
      await loadSecondaryComparison(app, primaryApp);
    } catch (err) {
      setError(t('error.searchFailed', { message: err.message }));
    }
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      compareApp = null;
      compareRows = [];
      clearBtn.hidden = true;
      input.value = '';
      syncUrl(primaryApp.trackId, null);
      renderResultsTable(primaryRows);
    });
  }
}

/**
 * Populate the B slot of the dual-app comparison using an already-resolved app.
 * This is the shared path for the compare form and Chart Browser secondary
 * action, so both render through the existing dual-app table contract.
 * @param {object} appB
 * @param {object} primaryApp
 */
async function loadSecondaryComparison(appB, primaryApp) {
  compareApp = appB;

  const toggle = document.getElementById('compare-toggle');
  if (toggle) toggle.textContent = t('compare.toggleClose');

  const form = document.getElementById('compare-form');
  if (form) form.hidden = false;

  const clearBtn = document.getElementById('compare-clear');
  if (clearBtn) clearBtn.hidden = false;

  const input = document.getElementById('compare-input');
  if (input && appB.trackName) input.value = appB.trackName;

  setLoading(t('status.loadingProgress', { current: 0, total: STOREFRONTS.length }));
  const streamedB = [];
  compareRows = await streamLookups(STOREFRONTS, lookupAppByCountry, appB.trackId, (_row, completed, total) => {
    streamedB.push(_row);
    setLoading(t('status.loadingProgress', { current: completed, total }));
    renderDualAppTable(primaryRows, [...streamedB], primaryApp, appB);
  });
  syncUrl(primaryApp.trackId, appB.trackId);
  renderDualAppTable(primaryRows, compareRows, primaryApp, appB);
  clearStatus();
}

/**
 * Resolve a Chart Browser entry into the B slot when a primary app is loaded.
 * Without a current primary comparison, preserve the Chart Browser's single-app
 * behavior by opening the selected app normally.
 * @param {string|number} trackId
 */
async function compareChartEntry(trackId) {
  if (!currentPrimaryApp || primaryRows.length === 0) {
    await runById(String(trackId));
    return;
  }

  setLoading(t('compare.loading'));
  try {
    const app = await lookupAppById(trackId, getEffectiveSearchRegion());
    if (!app) {
      setError(t('error.noAppId', { appId: trackId }));
      return;
    }
    await loadSecondaryComparison(app, currentPrimaryApp);
  } catch (err) {
    setError(t('error.loadById', { appId: trackId, message: err.message }));
  }
}

function clearSecondaryComparison() {
  compareApp = null;
  compareRows = [];
}

function isOpportunityRoute(pathname = window.location.pathname) {
  return routeName(pathname) === 'opportunities';
}

function isRadarRoute(pathname = window.location.pathname) {
  return routeName(pathname) === 'radar';
}

function isDailyInsightRoute(pathname = window.location.pathname) {
  return routeName(pathname) === 'daily-insight';
}

function routeName(pathname = window.location.pathname) {
  const parts = String(pathname)
    .split('/')
    .filter(Boolean)
    .map((part) => part.toLowerCase());
  return ['zh', 'zh-cn', 'zh-hant', 'en', 'ja'].includes(parts[0])
    ? parts[1]
    : parts[0];
}

function showStandaloneRoute(visibleId, routeClass) {
  document.body.classList.add(routeClass);
  document.querySelector('.hero')?.setAttribute('hidden', '');
  document.querySelector('.feature-highlights')?.setAttribute('hidden', '');

  const container = document.querySelector('.container');
  if (container) {
    for (const child of container.children) {
      if (child.id !== visibleId && !child.classList.contains('site-footer')) {
        child.setAttribute('hidden', '');
      }
    }
  }
}

function showOpportunityRoute() {
  showStandaloneRoute('opportunity-radar-page', 'route-opportunities');
  const page = document.getElementById('opportunity-radar-page');
  const options = {};
  const reload = () => loadOpportunityRadar(page, options);
  options.onRetry = reload;
  bindOpportunityRadarLocaleRefresh();
  reload();
}

function showRadarRoute() {
  showStandaloneRoute('chart-browser-container', 'route-radar');
  const chartBrowserContainer = document.getElementById('chart-browser-container');
  renderChartBrowser(
    chartBrowserContainer,
    (trackId) => {
      window.location.href = buildAppComparisonHref(trackId);
    },
    (trackId) => {
      window.location.href = buildAppComparisonHref(trackId);
    },
  );
}

function showDailyInsightRoute() {
  showStandaloneRoute('daily-insight-page', 'route-daily-insight');
  loadDailyInsightReport(document.getElementById('daily-insight-page'));
}

function init() {
  initTheme();
  initI18n();

  initRegionSelector();
  initGenreSelector();

  if (isOpportunityRoute()) {
    showOpportunityRoute();
    return;
  }
  if (isRadarRoute()) {
    showRadarRoute();
    return;
  }
  if (isDailyInsightRoute()) {
    showDailyInsightRoute();
    return;
  }

  const form = document.getElementById('search-form');
  const input = document.getElementById('search-input');
  if (!form || !input) return;

  // Silently replace a pasted App Store URL with just the numeric app ID.
  input.addEventListener('input', () => {
    const extracted = parseAppStoreUrl(input.value);
    if (extracted) input.value = extracted;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const query = input.value.trim();
    if (!query) return;
    // If the user somehow submits an App Store URL without triggering 'input'
    // (e.g. form autofill), handle it here too.
    const extractedId = parseAppStoreUrl(query);
    if (extractedId) {
      input.value = extractedId;
      runById(extractedId);
    } else {
      runSearch(query);
    }
  });

  // Wire up Chart Browser toggle button.
  const chartBrowserToggle = document.getElementById('chart-browser-toggle');
  const chartBrowserContainer = document.getElementById('chart-browser-container');
  let chartBrowserOpen = false;

  if (chartBrowserToggle && chartBrowserContainer) {
    chartBrowserToggle.addEventListener('click', () => {
      chartBrowserOpen = !chartBrowserOpen;
      if (chartBrowserOpen) {
        const closeChartBrowser = () => {
          hideChartBrowser(chartBrowserContainer);
          chartBrowserOpen = false;
        };
        renderChartBrowser(
          chartBrowserContainer,
          (trackId) => {
            closeChartBrowser();
            clearSecondaryComparison();
            runById(String(trackId));
          },
          (trackId) => {
            closeChartBrowser();
            compareChartEntry(trackId);
          },
        );
        hideExploreGrid(document.getElementById('explore-grid-container'));
      } else {
        hideChartBrowser(chartBrowserContainer);
      }
    });
  }

  // Open a shared link straight to its comparison; otherwise show Explore grid + idle hint.
  const initialParams = new URL(window.location.href).searchParams;
  const appId = initialParams.get('appId');
  if (hydrateOfflineComparison()) {
    // Offline restore already rendered the last useful comparison.
  } else if (appId && /^\d+$/.test(appId.trim())) {
    runById(appId.trim(), appCountryFromParams(initialParams, getEffectiveSearchRegion()));
  } else {
    setIdle(t('status.idle'));
    showExploreGrid();
  }

  renderLeaderboard();
  renderWatchlist((id) => runById(id));
  renderTrendingDigestPanel();
}

function renderTrendingDigestPanel() {
  const container = document.getElementById('trending-digest-container');
  if (!container) return;
  const watchlist = getWatchlist();
  const movers = computeMovers(watchlist, new Date().toISOString());
  renderTrendingDigest(movers, container, (trackId) => {
    hideExploreGrid(document.getElementById('explore-grid-container'));
    runById(trackId);
  });
}

init();
